import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    FiHome,
    FiPackage,
    FiUsers,
    FiFileText,
    FiPercent,
    FiLink,
    FiSettings,
    FiMenu,
    FiX,
    FiBarChart2,
    FiCheck,
    FiInbox,
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
    FiTrendingUp,
    FiBox
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import './Sidebar.css';

import companyService from '../../services/company.service';
import quotationsService from '../../services/quotations.service';
import requestsService from '../../services/requests.service';
import { useNotifications } from '../../context/NotificationContext';

const Sidebar = ({ isCollapsed, toggleSidebar, isMobileOpen, toggleMobileSidebar }) => {
    const location = useLocation();
    const { user } = useAuth();
    const { refreshTrigger } = useNotifications();
    const [showApps, setShowApps] = useState(false); // Dropdown Apps
    const [logoUrl, setLogoUrl] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [pendingCount, setPendingCount] = useState(0); // Borradores Públicos

    React.useEffect(() => {
        const fetchCompanyData = async () => {
            try {
                const data = await companyService.getMyCompany();
                setCompanyName(data.razon_social);
                if (data.logo) setLogoUrl(data.logo);
            } catch (error) {
                console.error("Error fetching company data:", error);
            }
        };
        fetchCompanyData();
    }, []);

    // Auto-refresh pending quotations count every 30 seconds
    useEffect(() => {
        const fetchPendingStats = async () => {
            try {
                console.log('Fetching pending stats...');
                const stats = await quotationsService.getPendingStats();
                console.log('Pending stats received:', stats);
                // Backend returns { count: N }, but checking both just in case
                setPendingCount(stats.count !== undefined ? stats.count : (stats.pendientes || 0));


                if (user?.rol === 'ADMIN') {
                    // Mantenido para lógica futura si se requieren notificaciones en Solicitudes
                }
            } catch (err) {
                console.error('Error fetching pending stats:', err);
            }
        };

        // Fetch inicial
        fetchPendingStats();

        // Polling cada 30 segundos
        const interval = setInterval(fetchPendingStats, 30000);

        // Cleanup
        return () => clearInterval(interval);
    }, [refreshTrigger, user]); // Se ejecuta cuando refreshTrigger cambia

    const menuItems = [
        { path: '/app/cotizaplus', icon: FiHome, label: 'Dashboard' },
        { path: '/app/cotizaplus/productos', icon: FiPackage, label: 'Productos' },
        { path: '/app/cotizaplus/clientes', icon: FiUsers, label: 'Clientes' },
        { path: '/app/cotizaplus/cotizaciones', icon: FiFileText, label: 'Cotizaciones', badge: pendingCount },
        { path: '/app/cotizaplus/impuestos', icon: FiPercent, label: 'Impuestos' },
        ...(user?.rol === 'ADMIN' ? [
            // { path: '/app/cotizaplus/ofertas', icon: FiGift, label: 'Ofertas' }, // Oculto - Futuro módulo CRM
        ] : []),
        // Panel de Solicitudes para el Empleado (sus propias solicitudes) - Solo visible si NO es admin
        ...(user?.rol !== 'ADMIN' ? [
            { path: '/app/cotizaplus/mis-solicitudes', icon: FiInbox, label: 'Mis Solicitudes' }
        ] : []),
        ...(user?.rol === 'ADMIN' ? [
            // { path: '/app/cotizaplus/ofertas', icon: FiGift, label: 'Ofertas' }, // Oculto por solicitud (Modulo CRM)
            { path: '/app/cotizaplus/reportes', icon: FiBarChart2, label: 'Reportes' },
            { path: '/app/cotizaplus/configuracion', icon: FiSettings, label: 'Configuración' },
        ] : []),
        { path: '/app/cotizaplus/autoatencion', icon: FiLink, label: 'Link Autoatención' },
    ];

    return (
        <>
            {/* Overlay para móvil */}
            {isMobileOpen && <div className="sidebar-overlay" onClick={toggleMobileSidebar} />}

            {/* Sidebar */}
            <aside className={`sidebar ${isMobileOpen ? 'sidebar-open' : ''} ${isCollapsed ? 'sidebar-collapsed' : ''}`}>

                {/* Botón de Colapso (Desktop) */}
                <button
                    className="sidebar-collapse-btn"
                    onClick={toggleSidebar}
                    title={isCollapsed ? "Mostrar Menú" : "Ocultar Menú"}
                >
                    {isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
                </button>

                <div className="sidebar-header">
                    <div className="sidebar-logo-container">
                        {/* Marca CotizaPlus */}
                        <div className="brand-header">
                            <h1 className="brand-title">CotizaPlus</h1>
                            <span className="brand-subtitle">by IdevPlus</span>
                        </div>

                        {/* App Switcher Toggle */}
                        <button
                            className={`app-switcher-toggle ${showApps ? 'active' : ''}`}
                            onClick={() => setShowApps(!showApps)}
                            title="Cambiar Aplicación"
                        >
                            <FiChevronDown />
                        </button>

                        {/* Dropdown Menu */}
                        {showApps && (
                            <div className="app-dropdown">
                                <div className="app-dropdown-header">Mis Aplicaciones</div>
                                <div className="app-dropdown-list">
                                    <div className="app-dropdown-item active">
                                        <div className="app-icon-small quote-icon">
                                            <FiTrendingUp />
                                        </div>
                                        <span>CotizaPlus</span>
                                        <FiCheck className="check-icon" />
                                    </div>
                                    <div className="app-dropdown-item disabled">
                                        <div className="app-icon-small crm-icon">
                                            <FiUsers />
                                        </div>
                                        <span>CRM Plus</span>
                                        <span className="badge-soon">Pronto</span>
                                    </div>
                                    <div className="app-dropdown-item disabled">
                                        <div className="app-icon-small inventory-icon">
                                            <FiBox />
                                        </div>
                                        <span>Inventario Plus</span>
                                        <span className="badge-soon">Pronto</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>


                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item) => {
                        const Icon = item.icon;

                        // Si es cotizaciones y hay pendientes, agregar query param al link
                        const isCotizaciones = item.path === '/app/cotizaplus/cotizaciones';
                        const linkTo = (isCotizaciones && pendingCount > 0)
                            ? `${item.path}?pending=true`
                            : item.path;

                        // Verificar active
                        const isActive = (item.path === '/app/cotizaplus' || item.path === '/')
                            ? location.pathname === item.path
                            : location.pathname.startsWith(item.path);

                        return (
                            <Link
                                key={item.path}
                                to={linkTo}
                                id={`tour-sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''}`}
                                onClick={() => { if (isMobileOpen) toggleMobileSidebar(); }}
                                style={{ position: 'relative' }}
                            >
                                <Icon size={20} />
                                <span>{item.label}</span>
                                {item.badge > 0 && (
                                    <span style={{
                                        position: 'absolute',
                                        right: '15px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'var(--danger-red)',
                                        color: 'white',
                                        borderRadius: '12px',
                                        padding: '2px 8px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        minWidth: '20px',
                                        textAlign: 'center'
                                    }}>
                                        {item.badge}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Usuario info (bottom) */}
                <div className="sidebar-user">
                    <div className="sidebar-user-avatar">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                    <div className="sidebar-user-info">
                        <p className="sidebar-user-name">{user?.first_name} {user?.last_name}</p>
                        <p className="sidebar-user-role">
                            {user?.rol === 'ADMIN' ? 'Administrador' : 'Empleado'}
                        </p>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
